import { useState, useRef } from "react";
import { X, Plus, Trash2, Loader2, Save, Upload, GripVertical, Video, Paperclip, Eye, Music, Radio, Compass } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

import RichTextEditor from "@/components/RichTextEditor";
const CATEGORIES = ["Personal Growth", "Recovery & Resilience", "Wellness", "Breathwork & Meditation", "Relationships", "Purpose & Stewardship", "Business & Enhancers"];
const MEMBER_ROLE_OPTS = [
  { v: "community", l: "Community members" },
  { v: "enhancer", l: "Enhancers" },
  { v: "staff", l: "Staff" },
  { v: "council", l: "Council" },
];
const GAUGE_TAGS = ["gratitude", "awareness", "unity", "growth", "energy"];
const LIVE_KINDS = [["", "No live session"], ["zoom", "Zoom"], ["meet", "Google Meet"], ["in_person", "In person at a center"]];

const blankLesson = () => ({ id: "", title: "", video_url: "", audio_url: "", body: "", attachments: [], duration_min: 0, is_preview: false, live_kind: "", live_url: "", live_at: "", live_location: "" });
const blankModule = () => ({ id: "", title: "", lessons: [blankLesson()] });

export default function CourseBuilderModal({ course, onClose, onSaved }) {
  const editing = !!course?.id;
  const [f, setF] = useState(() => ({
    title: course?.title || "", subtitle: course?.subtitle || "", description: course?.description || "",
    thumbnail: course?.thumbnail || "", category: course?.category || CATEGORIES[0],
    access: course?.access || "free", price: course?.price || 0, member_discount_pct: course?.member_discount_pct || 0,
    required_roles: course?.required_roles || [], drip_enabled: course?.drip_enabled || false,
    published: course?.published || false,
    billing_type: course?.billing_type || "one_time",
    sub_interval: course?.sub_interval || "month",
    sub_interval_count: course?.sub_interval_count || 1,
    gauge_tags: course?.gauge_tags || [],
    modules: course?.modules?.length ? course.modules : [blankModule()],
  }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [drag, setDrag] = useState(null); // {type:'module'|'lesson', mi, li}
  const thumbRef = useRef();

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const updModule = (mi, k, v) => setF((s) => ({ ...s, modules: s.modules.map((m, i) => i === mi ? { ...m, [k]: v } : m) }));
  const updLesson = (mi, li, k, v) => setF((s) => ({
    ...s, modules: s.modules.map((m, i) => i !== mi ? m : { ...m, lessons: m.lessons.map((l, j) => j === li ? { ...l, [k]: v } : l) }),
  }));
  const setPreview = (mi, li, on) => setF((s) => ({
    // One free preview per course: turning one on clears any other.
    ...s, modules: s.modules.map((m, i) => ({
      ...m, lessons: m.lessons.map((l, j) => ({ ...l, is_preview: on ? (i === mi && j === li) : (i === mi && j === li ? false : l.is_preview) })),
    })),
  }));
  const addModule = () => setF((s) => ({ ...s, modules: [...s.modules, blankModule()] }));
  const rmModule = (mi) => setF((s) => ({ ...s, modules: s.modules.filter((_, i) => i !== mi) }));
  const addLesson = (mi) => setF((s) => ({ ...s, modules: s.modules.map((m, i) => i === mi ? { ...m, lessons: [...m.lessons, blankLesson()] } : m) }));
  const rmLesson = (mi, li) => setF((s) => ({ ...s, modules: s.modules.map((m, i) => i !== mi ? m : { ...m, lessons: m.lessons.filter((_, j) => j !== li) }) }));

  // ---- Drag & drop reordering (modules + lessons within a module) ----
  const dropModule = (ti) => setF((s) => {
    if (!drag || drag.type !== "module" || drag.mi === ti) return s;
    const mods = [...s.modules];
    const [moved] = mods.splice(drag.mi, 1);
    mods.splice(ti, 0, moved);
    return { ...s, modules: mods };
  });
  const dropLesson = (mi, ti) => setF((s) => {
    if (!drag || drag.type !== "lesson" || drag.mi !== mi || drag.li === ti) return s;
    const mods = s.modules.map((m, i) => {
      if (i !== mi) return m;
      const ls = [...m.lessons];
      const [moved] = ls.splice(drag.li, 1);
      ls.splice(ti, 0, moved);
      return { ...m, lessons: ls };
    });
    return { ...s, modules: mods };
  });

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/enhancer/courses/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };
  const onThumb = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading("thumb");
    try { const d = await uploadFile(file); upd("thumbnail", d.url); toast.success("Thumbnail uploaded"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "We couldn't upload that file. Please check it and try again."); }
    finally { setUploading(""); }
  };
  const onMedia = async (mi, li, field, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(`${field}-${mi}-${li}`);
    try { const d = await uploadFile(file); updLesson(mi, li, field, d.url); toast.success("Uploaded"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "We couldn't upload that file. Please check it and try again."); }
    finally { setUploading(""); }
  };
  const onAttach = async (mi, li, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(`att-${mi}-${li}`);
    try {
      const d = await uploadFile(file);
      updLesson(mi, li, "attachments", [...(f.modules[mi].lessons[li].attachments || []), { name: d.name, url: d.url }]);
      toast.success("Attachment added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "We couldn't upload that file. Please check it and try again."); }
    finally { setUploading(""); }
  };
  const rmAttach = (mi, li, ai) => updLesson(mi, li, "attachments", f.modules[mi].lessons[li].attachments.filter((_, i) => i !== ai));
  const toggleRole = (r) => upd("required_roles", f.required_roles.includes(r) ? f.required_roles.filter((x) => x !== r) : [...f.required_roles, r]);
  const toggleTag = (t) => upd("gauge_tags", f.gauge_tags.includes(t) ? f.gauge_tags.filter((x) => x !== t) : [...f.gauge_tags, t]);

  const save = async () => {
    if (!f.title.trim()) return toast.error("Give your course a title.");
    setSaving(true);
    try {
      const payload = { ...f, price: parseFloat(f.price) || 0, member_discount_pct: parseInt(f.member_discount_pct) || 0, sub_interval_count: parseInt(f.sub_interval_count) || 1 };
      if (editing) await api.put(`/enhancer/courses/${course.id}`, payload);
      else await api.post("/enhancer/courses", payload);
      toast.success(editing ? "Course updated" : "Course created");
      onSaved?.();
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="course-builder">
      <div className="bg-white rounded-2xl w-full max-w-3xl my-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[#E5E0D8] px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="font-serif text-xl text-[#1A242B]">{editing ? "Edit course" : "New course"}</h2>
          <button onClick={onClose} data-testid="course-builder-close" className="text-[#4A5D6B] hover:text-[#1A242B]"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className="text-sm font-medium text-[#1A242B] mb-1 block">Title</label>
              <input value={f.title} onChange={(e) => upd("title", e.target.value)} data-testid="course-title" className="eyw-input w-full" placeholder="e.g. Intro to Breathwork" /></div>
            <div className="sm:col-span-2"><label className="text-sm font-medium text-[#1A242B] mb-1 block">Subtitle</label>
              <input value={f.subtitle} onChange={(e) => upd("subtitle", e.target.value)} className="eyw-input w-full" placeholder="One-line promise" /></div>
            <div className="sm:col-span-2"><label className="text-sm font-medium text-[#1A242B] mb-1 block">Description</label>
              <RichTextEditor value={f.description} onChange={(v) => upd("description", v)} testid="course-desc" placeholder="What learners will get from this course…" minHeight={120} enableBlocks={false} /></div>
            <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Category</label>
              <input list="course-cat-list" value={f.category} onChange={(e) => upd("category", e.target.value)} placeholder="Category (pick or type your own)" className="eyw-input w-full" />
              <datalist id="course-cat-list">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist></div>
            <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Thumbnail</label>
              <div className="flex items-center gap-3">
                {f.thumbnail && <img src={f.thumbnail} alt="thumb" className="h-11 w-16 object-cover rounded-md" />}
                <input type="file" accept="image/*" ref={thumbRef} onChange={onThumb} className="hidden" />
                <button type="button" onClick={() => thumbRef.current?.click()} data-testid="course-thumb-upload" className="eyw-btn-outline !py-2 text-sm">
                  {uploading === "thumb" ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} Upload</button>
              </div></div>
          </div>

          {/* GAUGE tags */}
          <div className="rounded-xl border border-[#E5E0D8] p-4">
            <label className="text-sm font-medium text-[#1A242B] flex items-center gap-2 mb-1"><Compass size={15} className="text-[#144F75]" /> GAUGE pillars this course supports</label>
            <p className="text-xs text-[#8FA89B] mb-2">Tagged courses are recommended to members whose GAUGE exploration matches these pillars.</p>
            <div className="flex flex-wrap gap-2">
              {GAUGE_TAGS.map((t) => (
                <button key={t} type="button" onClick={() => toggleTag(t)} data-testid={`course-gauge-${t}`}
                  className={`rounded-full px-3 py-1.5 text-xs capitalize ${f.gauge_tags.includes(t) ? "bg-[#144F75] text-white" : "border border-[#E5E0D8] text-[#4A5D6B]"}`}>{t}</button>
              ))}
            </div>
          </div>

          {/* Access & pricing */}
          <div className="rounded-xl border border-[#E5E0D8] p-4 space-y-3">
            <label className="text-sm font-medium text-[#1A242B] block">Access</label>
            <div className="flex flex-wrap gap-2">
              {[["free", "Free"], ["paid", "Paid"], ["membership", "Members only"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => upd("access", v)} data-testid={`course-access-${v}`}
                  className={`rounded-full px-4 py-2 text-sm ${f.access === v ? "bg-[#144F75] text-white" : "border border-[#E5E0D8] text-[#4A5D6B]"}`}>{l}</button>
              ))}
            </div>
            {f.access === "paid" && (
              <>
                <div className="flex flex-wrap gap-2">
                  {[["one_time", "One-time purchase"], ["subscription", "Recurring subscription"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => upd("billing_type", v)} data-testid={`course-billing-${v}`}
                      className={`rounded-full px-3.5 py-1.5 text-xs ${f.billing_type === v ? "bg-[#5C8A72] text-white" : "border border-[#E5E0D8] text-[#4A5D6B]"}`}>{l}</button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-[#4A5D6B] mb-1 block">{f.billing_type === "subscription" ? "Price per billing period (USD)" : "Price (USD)"}</label>
                    <input type="number" min="0" step="1" value={f.price} onChange={(e) => upd("price", e.target.value)} data-testid="course-price" className="eyw-input w-full" /></div>
                  <div><label className="text-xs text-[#4A5D6B] mb-1 block">Member discount %</label>
                    <input type="number" min="0" max="100" value={f.member_discount_pct} onChange={(e) => upd("member_discount_pct", e.target.value)} className="eyw-input w-full" /></div>
                </div>
                {f.billing_type === "subscription" && (
                  <div className="flex flex-wrap items-center gap-2 text-sm text-[#4A5D6B]" data-testid="course-sub-config">
                    Bills every
                    <input type="number" min="1" max="12" value={f.sub_interval_count} onChange={(e) => upd("sub_interval_count", e.target.value)} data-testid="course-sub-count" className="eyw-input !w-16 !py-1.5 text-sm" />
                    <select value={f.sub_interval} onChange={(e) => upd("sub_interval", e.target.value)} data-testid="course-sub-interval" className="eyw-input !py-1.5 text-sm">
                      <option value="week">week(s)</option>
                      <option value="month">month(s)</option>
                    </select>
                    <span className="text-xs text-[#8FA89B]">— charged via Stripe until the student cancels</span>
                  </div>
                )}
              </>
            )}
            {f.access === "membership" && (
              <div><p className="text-xs text-[#4A5D6B] mb-2">Included for these member levels (leave empty = all members):</p>
                <div className="flex flex-wrap gap-2">{MEMBER_ROLE_OPTS.map((r) => (
                  <button key={r.v} type="button" onClick={() => toggleRole(r.v)}
                    className={`rounded-full px-3 py-1.5 text-xs ${f.required_roles.includes(r.v) ? "bg-[#5C8A72] text-white" : "border border-[#E5E0D8] text-[#4A5D6B]"}`}>{r.l}</button>))}</div></div>
            )}
          </div>

          <div className="flex flex-wrap gap-6">
            <Toggle label="Sequential unlock (drip)" checked={f.drip_enabled} onChange={() => upd("drip_enabled", !f.drip_enabled)} testid="course-drip" />
            <Toggle label="Published" checked={f.published} onChange={() => upd("published", !f.published)} testid="course-published" />
          </div>

          {/* Curriculum */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-serif text-lg text-[#1A242B]">Curriculum</h3>
              <button type="button" onClick={addModule} data-testid="course-add-module" className="eyw-btn-outline !py-1.5 text-sm"><Plus size={15} /> Section</button>
            </div>
            <p className="text-xs text-[#8FA89B] mb-3">Drag the <GripVertical size={11} className="inline" /> handle to reorder sections and lessons. Mark one lesson as the free preview.</p>
            <div className="space-y-4">
              {f.modules.map((m, mi) => (
                <div key={m.id || mi} className={`rounded-xl border p-4 ${drag?.type === "module" && drag.mi === mi ? "border-[#144F75] opacity-60" : "border-[#E5E0D8]"}`}
                  data-testid={`course-module-${mi}`}
                  onDragOver={(e) => { if (drag?.type === "module") e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); dropModule(mi); setDrag(null); }}>
                  <div className="flex items-center gap-2 mb-3">
                    <span draggable onDragStart={() => setDrag({ type: "module", mi })} onDragEnd={() => setDrag(null)}
                      data-testid={`course-module-drag-${mi}`} className="cursor-grab active:cursor-grabbing text-[#8FA89B] hover:text-[#144F75]"><GripVertical size={16} /></span>
                    <input value={m.title} onChange={(e) => updModule(mi, "title", e.target.value)} placeholder={`Section ${mi + 1} title`} data-testid={`course-module-title-${mi}`} className="eyw-input flex-1 !py-2" />
                    {f.modules.length > 1 && <button type="button" onClick={() => rmModule(mi)} className="text-red-500"><Trash2 size={16} /></button>}
                  </div>
                  <div className="space-y-3 pl-6">
                    {m.lessons.map((l, li) => (
                      <div key={l.id || li} className={`rounded-lg bg-[#F9F7F3] p-3 ${drag?.type === "lesson" && drag.mi === mi && drag.li === li ? "ring-1 ring-[#144F75] opacity-60" : ""}`}
                        data-testid={`course-lesson-${mi}-${li}`}
                        onDragOver={(e) => { if (drag?.type === "lesson" && drag.mi === mi) { e.preventDefault(); e.stopPropagation(); } }}
                        onDrop={(e) => { if (drag?.type === "lesson" && drag.mi === mi) { e.preventDefault(); e.stopPropagation(); dropLesson(mi, li); setDrag(null); } }}>
                        <div className="flex items-center gap-2 mb-2">
                          <span draggable onDragStart={(e) => { e.stopPropagation(); setDrag({ type: "lesson", mi, li }); }} onDragEnd={() => setDrag(null)}
                            data-testid={`course-lesson-drag-${mi}-${li}`} className="cursor-grab active:cursor-grabbing text-[#8FA89B] hover:text-[#144F75]"><GripVertical size={14} /></span>
                          <input value={l.title} onChange={(e) => updLesson(mi, li, "title", e.target.value)} placeholder="Lesson title" data-testid={`course-lesson-title-${mi}-${li}`} className="eyw-input flex-1 !py-1.5 text-sm" />
                          <button type="button" onClick={() => rmLesson(mi, li)} className="text-red-500"><Trash2 size={14} /></button>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <Video size={14} className="text-[#144F75] shrink-0" />
                          <input value={l.video_url} onChange={(e) => updLesson(mi, li, "video_url", e.target.value)} placeholder="YouTube / Vimeo link or uploaded video URL" data-testid={`course-lesson-video-${mi}-${li}`} className="eyw-input flex-1 !py-1.5 text-sm" />
                          <label className="text-xs text-[#144F75] cursor-pointer whitespace-nowrap">
                            {uploading === `video_url-${mi}-${li}` ? <Loader2 className="animate-spin inline" size={12} /> : "Upload"}
                            <input type="file" accept="video/*" onChange={(e) => onMedia(mi, li, "video_url", e)} className="hidden" data-testid={`course-lesson-video-upload-${mi}-${li}`} />
                          </label>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <Music size={14} className="text-[#D98E73] shrink-0" />
                          <input value={l.audio_url} onChange={(e) => updLesson(mi, li, "audio_url", e.target.value)} placeholder="Audio URL (meditation, talk…)" data-testid={`course-lesson-audio-${mi}-${li}`} className="eyw-input flex-1 !py-1.5 text-sm" />
                          <label className="text-xs text-[#144F75] cursor-pointer whitespace-nowrap">
                            {uploading === `audio_url-${mi}-${li}` ? <Loader2 className="animate-spin inline" size={12} /> : "Upload"}
                            <input type="file" accept="audio/*" onChange={(e) => onMedia(mi, li, "audio_url", e)} className="hidden" data-testid={`course-lesson-audio-upload-${mi}-${li}`} />
                          </label>
                        </div>
                        <div className="mb-2"><RichTextEditor value={l.body} onChange={(v) => updLesson(mi, li, "body", v)} testid={`lesson-body-${mi}-${li}`} placeholder="Lesson content" minHeight={90} enableBlocks={false} /></div>
                        {/* Live session */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <Radio size={14} className="text-[#9E5C7F] shrink-0" />
                          <select value={l.live_kind || ""} onChange={(e) => updLesson(mi, li, "live_kind", e.target.value)} data-testid={`course-lesson-live-kind-${mi}-${li}`} className="eyw-input !py-1.5 text-xs">
                            {LIVE_KINDS.map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
                          </select>
                          {l.live_kind && (
                            <>
                              <input type="datetime-local" value={l.live_at || ""} onChange={(e) => updLesson(mi, li, "live_at", e.target.value)} data-testid={`course-lesson-live-at-${mi}-${li}`} className="eyw-input !py-1.5 text-xs" />
                              {l.live_kind === "in_person"
                                ? <input value={l.live_location || ""} onChange={(e) => updLesson(mi, li, "live_location", e.target.value)} placeholder="Center / address" data-testid={`course-lesson-live-location-${mi}-${li}`} className="eyw-input flex-1 min-w-[130px] !py-1.5 text-xs" />
                                : <input value={l.live_url || ""} onChange={(e) => updLesson(mi, li, "live_url", e.target.value)} placeholder={l.live_kind === "zoom" ? "Zoom link" : "Google Meet link"} data-testid={`course-lesson-live-url-${mi}-${li}`} className="eyw-input flex-1 min-w-[130px] !py-1.5 text-xs" />}
                            </>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {(l.attachments || []).map((a, ai) => (
                            <span key={ai} className="inline-flex items-center gap-1 text-xs bg-white border border-[#E5E0D8] rounded-full px-2 py-1">
                              <Paperclip size={11} /> {a.name} <button type="button" onClick={() => rmAttach(mi, li, ai)}><X size={11} className="text-red-500" /></button>
                            </span>
                          ))}
                          <label className="inline-flex items-center gap-1 text-xs text-[#144F75] cursor-pointer">
                            {uploading === `att-${mi}-${li}` ? <Loader2 className="animate-spin" size={12} /> : <Paperclip size={12} />} Add file (PDF…)
                            <input type="file" onChange={(e) => onAttach(mi, li, e)} className="hidden" />
                          </label>
                          <label className="inline-flex items-center gap-1 text-xs text-[#4A5D6B] cursor-pointer ml-auto">
                            <input type="checkbox" checked={!!l.is_preview} onChange={(e) => setPreview(mi, li, e.target.checked)} data-testid={`course-lesson-preview-${mi}-${li}`} />
                            <Eye size={12} /> Free preview
                          </label>
                        </div>
                      </div>
                    ))}
                    <button type="button" onClick={() => addLesson(mi)} data-testid={`course-add-lesson-${mi}`} className="text-sm text-[#144F75] flex items-center gap-1"><Plus size={14} /> Add lesson</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E5E0D8] px-6 py-4 flex justify-end gap-3 rounded-b-2xl">
          <button onClick={onClose} className="eyw-btn-outline">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="course-save" className="eyw-btn disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={16} />} <Save size={16} /> {editing ? "Save changes" : "Create course"}</button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <span className="text-sm font-medium text-[#1A242B]">{label}</span>
      <button type="button" onClick={onChange} data-testid={testid} className={`relative h-6 w-11 rounded-full transition-colors ${checked ? "bg-[#144F75]" : "bg-[#E5E0D8]"}`}>
        <span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all ${checked ? "left-6" : "left-1"}`} />
      </button>
    </label>
  );
}
